import React, { useState, useEffect, useRef } from "react";
import * as d3 from "d3";
import ChartTooltip, { getTooltipHTML } from "../ChartTooltip";
import "./NormalizedStackedBarChart.scss";
import GraphDownloadButton from "../../Button/GraphDownloadButton/GraphDownloadButton";
import Loader from "../../Loader/Loader";
import { getContrastColor, wrapText } from "../ChartUtil";
import DataNotFound from "../../DataNotFound/DataNotFound";
import ErrorBoundary from "../../ErrorBoundary/ErrorBoundary";

const NormalizedStackedBarChartInner = ({
  chartData,
  chartConfig,
  chartType,
  title,
  legentPosition = "top",
  colorArray = [],
}) => {
  const HEIGHT = legentPosition === "top" ? 120 : 320;

  const svgRef = useRef(null);
  const wrapperRef = useRef();
  const tooltipRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dimensions, setDimensions] = useState({ width: 900, height: HEIGHT });
  const [data, setData] = useState(null);

  useEffect(() => {
    if (chartData) {
      setData(() => {
        return chartData?.map((item, index) => ({
          label: item[chartConfig.key_field],
          value: item[chartConfig.value],
          percentage: item[chartConfig.percentage],
          activeValue:
            chartType === "percentage"
              ? item[chartConfig.percentage]
              : item[chartConfig.value],
        }));
      });
    } else {
      // API error, wrong response shape, or missing config — clear data so we show DataNotFound
      setData([]);
    }
  }, [chartData, chartConfig]);

  useEffect(() => {
    setIsLoading(true);
    if (data) {
      setIsLoading(false);
    }
  }, [data]);

  // Resize Observer
  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      if (!entries || !entries.length) return;
      const { width } = entries[0].contentRect;
      setDimensions({ width, height: HEIGHT });
    });

    resizeObserver.observe(wrapperRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (!data) return;
    const { width, height } = dimensions;
    d3.select(svgRef.current).selectAll("*").remove();

    const margin = { top: 40, right: 5, bottom: 20, left: 5 };
    const innerWidth = width; //- margin.left - margin.right;
    const barHeight = 68;

    const svg = d3
      .select(svgRef.current)
      .attr("width", width)
      .attr("height", height);

    const g = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    /* COLORS (match your image style) */
    const color = d3
      .scaleOrdinal()
      .domain(data.map((d) => d.label))
      .range([
        ...colorArray,
        // "#0E6A8A",
        // "#A7640A",
        // "#067A46",
        // "#C23B13",
        // "#3A4476",
      ]);

    /* NORMALIZE TO 100% */
    let cumulative = 0;
    const normalized = data.map((d) => {
      const start = cumulative;
      cumulative += Number(d.activeValue);
      return {
        ...d,
        start,
        end: cumulative,
      };
    });
    const total =
      chartType === "percentage"
        ? d3.sum(data, (d) => d.percentage)
        : d3.sum(data, (d) => d.value);

    const x = d3
      .scaleLinear()
      .domain([0, total])
      .range([0, innerWidth - 10]);

    /* BARS */
    g.selectAll("rect")
      .data(normalized)
      .enter()
      .append("rect")
      .attr("x", (d) => x(d.start))
      .attr("y", 0)
      .attr("height", barHeight)
      .attr("width", (d) => x(d.end) - x(d.start))
      .attr("fill", (d) => color(d.label))
      .on("mousemove", (event, d) => {
        const wrapperRect = wrapperRef.current.getBoundingClientRect();
        const tooltip = d3.select(tooltipRef.current);

        // set content first, then measure — otherwise height/width are stale
        tooltip
          .style("opacity", 0)
          .html(
            getTooltipHTML({
              label: d.label,
              Value: Math.round(d.value),
              Percentage: d.percentage ? `${Math.round(d.percentage)}%` : null,
            }),
          );

        const { width, height } = tooltip.node().getBoundingClientRect();

        let x = event.clientX - wrapperRect.left + 12;
        let y = event.clientY - wrapperRect.top - height - 10;

        // keep tooltip inside the wrapper bounds
        if (x + width > wrapperRect.width) x -= width + 24;
        if (y < 0) y = event.clientY - wrapperRect.top + 10;

        tooltip
          .style("opacity", 1)
          .style("left", `${x}px`)
          .style("top", `${y}px`);
      })
      .on("mouseleave", () => {
        // tooltip.style("opacity", 0)
        d3.select(tooltipRef.current).style("opacity", 0);
      });

    g.selectAll("text")
      .data(normalized)
      .enter()
      .append("text")
      .attr("x", (d) => x(d.start) + (x(d.end) - x(d.start)) / 2)
      .attr("y", barHeight / 2)
      .attr("dy", "0.35em")
      .attr("text-anchor", "middle")
      .style("fill", (d) => getContrastColor(color(d.label)))
      // .style("font-size", "14px")
      // .style("font-weight", "600")
      .style("font-size", "13px")
      .style("font-weight", 600)
      .text((d) => {
        const segmentWidth = x(d.end) - x(d.start);

        const value =
          chartType === "percentage"
            ? `${Math.round(d.activeValue)}%`
            : Math.round(d.activeValue);

        // Minimum width threshold (adjust as needed)
        const minWidth = 20;

        if (segmentWidth < minWidth || Math.round(d.activeValue) <= 0) {
          return "";
        }

        return value.toLocaleString("en-US");
      });

    if (legentPosition === "top") {
      /* LEGEND */
      const legend = svg
        .append("g")
        .attr("transform", `translate(${margin.left}, 10)`);

      const legendItem = legend
        .selectAll(".legend-item")
        .data(data)
        .enter()
        .append("g")
        .attr("class", "legend-item")
        .attr(
          "transform",
          (d, i) => `translate(${i * (width / data?.length)}, 0)`,
        );

      legendItem
        .append("rect")
        .attr("width", 14)
        .attr("height", 14)
        .attr("fill", (d) => color(d.label));

      legendItem
        .append("text")
        .attr("x", 20)
        .attr("y", 12)
        .style("font-size", "13px")
        .style("fill", "#6B7280")
        .text((d) => d.label);
    } else {
      /* LEGEND */
      const legend = svg
        .append("g")
        .attr(
          "transform",
          `translate(${margin.left}, ${barHeight + margin.top + margin.bottom})`,
        );

      const itemsPerColumn = Math.ceil(data.length / 2);
      const columnWidth = innerWidth / 2; // space between columns
      const rowHeight = 35; // vertical spacing

      const legendItem = legend
        .selectAll(".legend-item")
        .data(data)
        .enter()
        .append("g")
        .attr("class", "legend-item")
        .attr("transform", (d, i) => {
          const column = Math.floor(i / itemsPerColumn);
          const row = i % itemsPerColumn;

          return `translate(${column * columnWidth}, ${row * rowHeight})`;
        });

      legendItem
        .append("rect")
        .attr("width", 14)
        .attr("height", 14)
        .attr("rx", 2)
        .attr("fill", (d) => color(d.label));

      legendItem
        .append("text")
        .attr("x", 22)
        .attr("y", 12)
        .style("fill", "#4B5563")
        .each(function (d) {
          const text = d3.select(this);
          const fullValue = d.activeValue.toLocaleString("en-US");

          text.text(`${d.label}: `);

          text.attr("dy", "0").call(wrapText, columnWidth - 70, 2, true, 26);

          const lastTspan = text
            .selectAll("tspan")
            .filter(function (d, i, nodes) {
              return i === nodes.length - 1;
            });

          lastTspan
            .append("tspan")
            .text(` ${fullValue}`)
            .style("font-weight", "700");

          // 5. Update the tooltip to show the full combined string
          text.select("title").text(`${d.label}: ${fullValue}`);
        });
    }
  }, [data, dimensions]);

  return (
    <div
      className="normalized-stacked-bar-chart"
      ref={wrapperRef}
      style={{ width: "100%", minWidth: "768px", position: "relative" }}
    >
      <GraphDownloadButton
        svg={svgRef.current}
        fileName={title}
        jsonData={data}
      />

      {isLoading ? (
        <Loader />
      ) : chartData?.length > 0 ? (
        <div className="mt-4">
          <svg ref={svgRef} />
        </div>
      ) : (
        <DataNotFound />
      )}
      <ChartTooltip ref={tooltipRef} />
    </div>
  );
};

const NormalizedStackedBarChart = (props) => (
  <ErrorBoundary>
    <NormalizedStackedBarChartInner {...props} />
  </ErrorBoundary>
);

export default NormalizedStackedBarChart;
